import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getUser, setPage, setUser, useHub } from "../../../../core/Global";
import { DrawerForm } from "../../../../components/drawers/DrawerForm";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import TextBox from "../../../../components/form/TextBox";
import Icon from "../../../../components/base/Icon";
import Modal from "../../../../components/base/Modal";
import Indicator from "../../../../components/base/Indicator";
import "./AccountSecurity.css";


interface SecurityInfo {
    username?: string
    email?: string
    googleConnected?: boolean
    googleEmail?: string
    twoFactorEnabled?: boolean
}

type EditKind = "username" | "email" | "password"


// One line of the page: what the thing is, what it says now, and the control that changes it.
// The placeholder is greyed only when it is standing in for a missing value -- a real email is a
// fact about the account and should read like one.
function SecurityRow(props: {
    icon: any, title: string, value?: string, placeholder?: string,
    action: string, onAction: () => void
}) {
    const empty = !props.value
    return (
        <div className="sec-row">
            <div className="sec-row-icon">
                <Icon icon={props.icon} type="solid" size={16} />
            </div>
            <div className="sec-row-body">
                <div className="sec-row-title">{props.title}</div>
                <div className={"sec-row-value" + (empty ? " is-muted" : "")}>
                    {props.value || props.placeholder}
                </div>
            </div>
            <button type="button" className="btn btn-sm btn-light btn-active-light-primary"
                onClick={e => { e.preventDefault(); props.onAction() }}>
                {props.action}
            </button>
        </div>
    )
}


// Your own security settings.
//
// Personal, not account-wide: whoever is signed in sees their own username, their own email and
// their own Google link. A dispatcher reading this page is reading about themselves.
//
// Two halves. How you sign in -- the things that are already true and can be changed -- and what
// protects it. They are kept apart because the first is maintenance and the second is a decision.
export default function AccountSecurity(props: {
    /** Left off where the surrounding view already names the page -- /profile does. */
    keepPageTitle?: boolean
} = {}) {

    const hub = useHub()
    const user = getUser()
    const [params, paramsSet] = useSearchParams()

    const [info, infoSet] = useState<SecurityInfo>()
    const [loading, loadingSet] = useState<boolean>(true)

    const [edit, editSet] = useState<EditKind>()

    const [connecting, connectingSet] = useState<boolean>(false)

    useEffect(() => {
        if (!props.keepPageTitle) setPage("Security", "Account", "System")
    }, [])

    const fetch = () => {
        loadingSet(true)
        hub.Get("/accounts/profile/security", res => {
            loadingSet(false)
            if (res.success) infoSet(res.data)
        }, () => loadingSet(false))
    }

    useEffect(fetch, [])

    // Google sends the browser back here with the outcome in the query rather than in a response,
    // because the round trip leaves the app entirely. Read once, said once, and cleared, so a
    // refresh does not repeat it.
    useEffect(() => {
        const outcome = params.get("google")
        if (!outcome) return

        if (outcome === "connected") toastSuccess("Your Google account has been connected.")
        else if (outcome === "taken") toastError("That Google account is already connected to another user.")
        else if (outcome === "expired") toastError("That took too long. Please try connecting again.")
        else toastError("Google sign-in could not be completed.")

        params.delete("google")
        paramsSet(params, { replace: true })
        fetch()
    }, [params])


    const connectGoogle = () => {
        connectingSet(true)
        hub.Post("/accounts/profile/google/connect", {}, res => {
            if (res.success && res.data?.url) { document.location.href = res.data.url; return }
            connectingSet(false)
            toastError(res.message ?? "Couldn't start connecting to Google.")
        }, () => {
            connectingSet(false)
            toastError("Couldn't start connecting to Google.")
        })
    }

    const disconnectGoogle = (callback: any) => {
        hub.Post("/accounts/profile/google/disconnect", {}, res => {
            if (res.success) { toastSuccess("Your Google account has been disconnected."); fetch() }
            else toastError(res.message)
            callback(!res.success)
        }, () => callback(true))
    }


    const save = (kind: EditKind, body: any, callback: any) => {

        if (kind === "username") {
            if (window.isNullOrEmpty(body.username)) { toastError("Please write your new username."); callback({ success: false }); return }
            if (window.isNullOrEmpty(body.password)) { toastError("Please confirm your current password."); callback({ success: false }); return }

            hub.Put("/accounts/profile/username", body, res => {
                if (res.success) { toastSuccess("Your username has been changed."); fetch() }
                else toastError(res.message)
                callback(res)
            }, () => callback({ success: false }))
            return
        }

        if (kind === "email") {
            if (!window.isValidEmail(body.email)) { toastError("Please write a valid email address."); callback({ success: false }); return }
            if (window.isNullOrEmpty(body.password)) { toastError("Please confirm your current password."); callback({ success: false }); return }

            hub.Put("/accounts/profile/email", body, res => {
                if (res.success) {
                    toastSuccess("Your email address has been changed.")
                    setUser(res.data)
                    fetch()
                }
                else toastError(res.message)
                callback(res)
            }, () => callback({ success: false }))
            return
        }

        if (window.isNullOrEmpty(body.currentPassword)) { toastError("Please write your current password."); callback({ success: false }); return }
        if (window.isNullOrEmpty(body.newPassword)) { toastError("Please write your new password."); callback({ success: false }); return }
        if (body.newPassword !== body.confirmPassword) { toastError("The two new passwords do not match."); callback({ success: false }); return }

        hub.Put("/accounts/users/profile/password/" + user?.id, body, res => {
            if (res.success) toastSuccess("Your password has been changed.")
            else toastError(res.message)
            callback(res)
        }, () => callback({ success: false }))
    }


    return (
        <>
            <div className="card mb-5 mb-xl-10">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">How you sign in</h3>
                    </div>
                </div>
                <div className="card-body border-top p-9">
                    {
                        loading ? <Indicator /> :
                            <div className="sec-rows">
                                <SecurityRow icon="at" title="Username" value={info?.username}
                                    action="Change" onAction={() => editSet("username")} />
                                <SecurityRow icon="envelope" title="Email address" value={info?.email}
                                    placeholder="Not set" action="Change" onAction={() => editSet("email")} />
                                <SecurityRow icon="key" title="Password" value="••••••••"
                                    action="Change" onAction={() => editSet("password")} />
                            </div>
                    }
                </div>
            </div>

            <div className="card mb-5 mb-xl-10">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">Connected accounts</h3>
                    </div>
                </div>
                <div className="card-body border-top p-9">
                    {
                        loading ? <Indicator /> :
                            <div className="sec-provider">
                                <img src="/assets/media/svg/brand-logos/google-icon.svg"
                                    className="sec-provider-logo" alt="Google" />
                                <div className="sec-provider-body">
                                    <div className="sec-row-title">Google</div>
                                    <div className={"sec-row-value" + (info?.googleConnected ? "" : " is-muted")}>
                                        {
                                            info?.googleConnected
                                                ? info.googleEmail
                                                : "Sign in with your Google account instead of a password"
                                        }
                                    </div>
                                </div>
                                {
                                    info?.googleConnected ?
                                        <div className="sec-provider-actions">
                                            <span className="badge badge-light-success fw-bold">
                                                <Icon icon="circle-check" type="solid" size={10} className="me-1" />
                                                Connected
                                            </span>
                                            <button type="button"
                                                className="btn btn-sm btn-light btn-active-light-danger"
                                                onClick={e => { e.preventDefault(); window.openModal("modal-google-disconnect") }}>
                                                Disconnect
                                            </button>
                                        </div>
                                        :
                                        <button type="button" className="btn btn-sm btn-primary"
                                            disabled={connecting}
                                            onClick={e => { e.preventDefault(); connectGoogle() }}>
                                            {connecting ? <Icon icon="spinner-third" spin className="me-2" /> : null}
                                            Connect
                                        </button>
                                }
                            </div>
                    }
                </div>
            </div>

            <div className="card mb-5 mb-xl-10">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">Two-factor authentication</h3>
                    </div>
                </div>
                <div className="card-body border-top p-9">
                    {
                        /* Described but not offered. Showing what it will be is worth more than an
                           empty section, and a switch that did nothing would be worse than both. */
                    }
                    <div className="sec-provider is-soon">
                        <div className="sec-row-icon">
                            <Icon icon="shield-halved" type="solid" size={16} />
                        </div>
                        <div className="sec-provider-body">
                            <div className="sec-row-title">
                                Verification code
                                <span className="badge badge-light-primary fw-bold ms-3">Coming soon</span>
                            </div>
                            <div className="sec-row-value is-muted">
                                A code sent to you at sign-in, so a password on its own is not enough
                                to get in.
                            </div>
                        </div>
                        <button type="button" className="btn btn-sm btn-light" disabled>
                            Enable
                        </button>
                    </div>
                </div>
            </div>


            {
                edit === "username" &&
                <DrawerForm id="drawer-change-username"
                    title="Change username" titleIcon="at"
                    buttonText="Save"
                    onClose={() => editSet(undefined)}
                    onSubmit={(body, callback) => save("username", body, callback)}>
                    <TextBox label="Current username" block value={info?.username} readOnly />
                    <TextBox label="New username" name="username" block autoFocus />
                    <TextBox label="Confirm your password" name="password" password block />
                </DrawerForm>
            }

            {
                edit === "email" &&
                <DrawerForm id="drawer-change-email"
                    title="Change email address" titleIcon="envelope"
                    buttonText="Save"
                    onClose={() => editSet(undefined)}
                    onSubmit={(body, callback) => save("email", body, callback)}>
                    <TextBox label="Current email address" block value={info?.email} readOnly />
                    <TextBox label="New email address" name="email" block autoFocus />
                    <TextBox label="Confirm your password" name="password" password block />
                </DrawerForm>
            }

            {
                edit === "password" &&
                <DrawerForm id="drawer-change-password"
                    title="Change password" titleIcon="key"
                    buttonText="Save"
                    onClose={() => editSet(undefined)}
                    onSubmit={(body, callback) => save("password", body, callback)}>
                    <TextBox label="Current password" name="currentPassword" password block autoFocus />
                    <TextBox label="New password" name="newPassword" password block />
                    <TextBox label="Confirm new password" name="confirmPassword" password block />
                </DrawerForm>
            }

            <Modal id="modal-google-disconnect"
                title="Disconnect Google"
                buttonText="Disconnect" buttonColor="danger"
                onConfirm={callback => disconnectGoogle(callback)}>
                <p className="mb-0">
                    You will no longer be able to sign in with <b>{info?.googleEmail}</b>. Your
                    password still works, so this will not lock you out.
                </p>
            </Modal>
        </>
    )
}
